'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "description", "editorSubtitle", "getIsUngated", "handleGoBack", "isBackButtonDisabled", "isBodyHeightFixed", "isXoEnabled", "onEditFieldClick", "onSubmit", "renderFieldLink", "saveLabel", "showEditField", "style", "title", "url", "value"];
import { ESC } from 'UIComponents/constants/KeyCodes';
import { isValidOperator } from 'customer-data-filters/filterQueryFormat/operator/Operator';
import ApplePrivacyEmailFilterBanner from '../ApplePrivacyEmailFilterBanner';
import FilterOperatorType from 'customer-data-filters/components/propTypes/FilterOperatorType';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropTypes from 'prop-types';
import { Component } from 'react';
import UIBackButton from 'UIComponents/nav/UIBackButton';
import UIButton from 'UIComponents/button/UIButton';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UIForm from 'UIComponents/form/UIForm';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UILink from 'UIComponents/link/UILink';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import isString from 'transmute/isString';
import styled, { css } from 'styled-components';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const wrapperComponentMixin = css(["display:flex;flex-direction:column;flex-grow:1;overflow-y:hidden;height:0;max-height:'100%';"]);
const StyledTitle = styled.h5.withConfig({
  displayName: "FilterEditorPanel__StyledTitle",
  componentId: "sc-1rvbn4n-0"
})(["word-wrap:break-word;"]);
const WrapperComponentBase = styled.div.withConfig({
  displayName: "FilterEditorPanel__WrapperComponentBase",
  componentId: "sc-1rvbn4n-1"
})(["", ";"], wrapperComponentMixin);
const WrapperComponentAsForm = styled(UIForm).withConfig({
  displayName: "FilterEditorPanel__WrapperComponentAsForm",
  componentId: "sc-1rvbn4n-2"
})(["", ""], wrapperComponentMixin);
const StyledUIScrollContainer = styled(UIScrollContainer).withConfig({
  displayName: "FilterEditorPanel__StyledUIScrollContainer",
  componentId: "sc-1rvbn4n-3"
})(["display:flex;flex-direction:column;flex:", ";overflow:hidden;"], ({
  isBodyHeightFixed
}) => isBodyHeightFixed ? 1 : undefined);
StyledUIScrollContainer.propTypes = {
  isBodyHeightFixed: PropTypes.bool.isRequired
};
const StyledUIScrollContainerInner = styled(UIScrollContainer.defaultProps.ScrollContainer).withConfig({
  displayName: "FilterEditorPanel__StyledUIScrollContainerInner",
  componentId: "sc-1rvbn4n-4"
})(["display:flex;flex-direction:column;padding:0 1px;"]);
export default class FilterEditorPanel extends Component {
  constructor(...args) {
    super(...args);
    this.handleKeyUp = evt => {
      const {
        handleGoBack,
        isBackButtonDisabled
      } = this.props;
      if (!isBackButtonDisabled && evt.keyCode === ESC) {
        evt.stopPropagation();
        handleGoBack(evt);
      }
    };
  }
  renderFixedHeader() {
    const {
      handleGoBack,
      isBackButtonDisabled,
      isXoEnabled
    } = this.props;
    return !isBackButtonDisabled ? /*#__PURE__*/_jsx(UIBackButton, {
      className: 'flex-shrink-0' + (!isXoEnabled ? " m-bottom-3" : ""),
      children: /*#__PURE__*/_jsx(UILink, {
        onClick: handleGoBack,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFilters.FilterEditorPanel.back"
        })
      })
    }) : null;
  }
  renderInnerHeader() {
    const {
      description,
      editorSubtitle,
      isXoEnabled,
      onEditFieldClick,
      renderFieldLink,
      showEditField,
      title,
      url,
      value
    } = this.props;
    const editorTitle = isXoEnabled && showEditField ? /*#__PURE__*/_jsxs("span", {
      children: [title, /*#__PURE__*/_jsx(UIIconButton, {
        className: "p-left-2",
        "data-test-id": "edit-field-button",
        onClick: onEditFieldClick,
        placement: "right",
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFilters.FilterEditorPanel.changeCriteria"
        }),
        use: "link",
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "edit"
        })
      })]
    }) : title;
    return /*#__PURE__*/_jsxs(View, {
      as: "span",
      marginBlockEnd: "300",
      children: [!isXoEnabled && editorSubtitle && /*#__PURE__*/_jsx("span", {
        className: "flex-shrink-0",
        children: editorSubtitle
      }), /*#__PURE__*/_jsx(StyledTitle, {
        className: "m-bottom-0",
        children: editorTitle
      }), isXoEnabled && description && /*#__PURE__*/_jsx(UIExpandableText, {
        children: /*#__PURE__*/_jsx(Text, {
          as: "p",
          marginBlockEnd: "0",
          children: description
        })
      }), isXoEnabled && isString(url) ? renderFieldLink({
        url,
        value
      }) : null]
    });
  }
  renderFixedFooter() {
    const {
      isXoEnabled,
      saveLabel,
      value
    } = this.props;
    return /*#__PURE__*/_jsx("footer", {
      className: "flex-shrink-0 align-baseline m-top-3 m-bottom-4",
      children: /*#__PURE__*/_jsx(UIButton, {
        block: !isXoEnabled,
        className: isXoEnabled ? 'flex-shrink-0' : "",
        "data-selenium-test": "apply-filter-btn",
        disabled: !isValidOperator(value),
        size: "small",
        type: "submit",
        use: "tertiary",
        children: saveLabel
      })
    });
  }
  render() {
    const _this$props = this.props,
      {
        children,
        getIsUngated,
        isBodyHeightFixed,
        isXoEnabled,
        onSubmit,
        style,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const WrapperComponent = typeof onSubmit === 'function' ? WrapperComponentAsForm : WrapperComponentBase;
    return /*#__PURE__*/_jsxs(WrapperComponent, Object.assign({}, rest, {
      onKeyUp: this.handleKeyUp,
      onSubmit: onSubmit,
      style: Object.assign({
        marginRight: isXoEnabled ? undefined : '-12px'
      }, style),
      children: [this.renderFixedHeader(), /*#__PURE__*/_jsxs(StyledUIScrollContainer, {
        ScrollContainer: StyledUIScrollContainerInner,
        isBodyHeightFixed: isBodyHeightFixed,
        scrollDirection: isBodyHeightFixed ? 'none' : 'vertical',
        children: [/*#__PURE__*/_jsx(ApplePrivacyEmailFilterBanner, {
          condition: value,
          getIsUngated: getIsUngated
        }), this.renderInnerHeader(), children]
      }), value && this.renderFixedFooter()]
    }));
  }
}
FilterEditorPanel.defaultProps = {
  isBodyHeightFixed: false,
  isXoEnabled: false,
  showEditField: false
};
FilterEditorPanel.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  description: PropTypes.string,
  editorSubtitle: PropTypes.node,
  getIsUngated: PropTypes.func,
  handleGoBack: PropTypes.func.isRequired,
  isBackButtonDisabled: PropTypes.bool,
  isBodyHeightFixed: PropTypes.bool.isRequired,
  isXoEnabled: PropTypes.bool,
  onEditFieldClick: PropTypes.func,
  onSubmit: PropTypes.func,
  renderFieldLink: PropTypes.func.isRequired,
  saveLabel: PropTypes.node.isRequired,
  showEditField: PropTypes.bool.isRequired,
  style: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  title: PropTypes.node.isRequired,
  url: PropTypes.string,
  value: FilterOperatorType
};